<template>
    <div class="mmp-lat-long-container">
        <div class="icon-container">
            <fa :icon="['fas', 'search']" class="search-icon" @click="zoomToCoords"/>
        </div>
        <div class="search-container">
            <input type="text" style="display:none" autocomplete="username">
            <b-form-input 
              v-model="coordinateInput" 
              class="input-area" 
              placeholder="Lat, Lon or Lat Lon"
              autocomplete="off"
              @keydown.enter="zoomToCoords"
            />
        </div>
    </div>
</template>
<style src="../Data_Tools/Data_Tools.css" scoped></style>
  
<style>
.mmp-lat-long-container {
    width: 200px;
    height: 40px;
    background-color:#063c6d;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row; 
    border-radius: 5px;
    border: 2px #063c6d solid;
}
.icon-container {
    height: 100%;
    width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
}
.search-container {
    color: white;
    width: 200px;
    height: 75%;
    padding-left: 5px;
}

.search-container .input-area {
    font-size: 0.7rem !important;
}

.input-area:focus {
    border: none !important;
    border-bottom: 2px white solid !important; 
    border-radius: 0 !important;
    color: white;
    background-color: transparent;
    box-shadow: none !important;
}

.input-area::-webkit-input-placeholder {
  color: white;
  opacity: 0.8;
}
.input-area {
    width: 93%;
    height: 100%;
    border: none !important;
    border-bottom: 2px white solid !important; 
    border-radius: 0 !important;
    color: white;
    background-color: transparent;
    font-weight: 500;
    padding-left: 5px;
}
.search-icon {
    cursor: pointer;
}
.search-icon:hover {
    opacity: 0.8;
}
.dot {
  height: 65px;
  width: 70px;
  background-color: #ffffff;
  color: #063c6d;
  border-radius: 50%;
  display: inline-block;
}
</style>

<script>
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import Style from 'ol/style/Style';
import RegularShape from 'ol/style/RegularShape';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import {fromLonLat, transform} from 'ol/proj'

export default {
    name: 'MMP_LatLong',
    data() {
        return {
            coordinateInput: null,
            format: false
        }
    },
    props: [
        'showToggle', 
        'ansiComponent', 
        'drtComponents', 
        'map', 
        'selectedExtent',
        'clickedCoords'
    ],
    methods: {
        async zoomToCoords() {
            const nums = this.parseCoordinateInput(this.coordinateInput)
            if (!nums) {
                this.$bvToast.toast(`Could not find two coordinates in input.`, {
                autoHideDelay: 7000,
                title: 'Error: Invalid Coordinates.',
                toaster: 'b-toaster-top-right'
                })
                return
            }

            const [a, b] = nums
            const detectedFormat = this.detectCoordFormat(a, b)

            if (detectedFormat === null) {
                const region = this.selectedExtent || 'the current region'
                this.$bvToast.toast(
                `Coordinates do not fall within ${region}. Please enter coordinates within the selected region.`,
                {
                    autoHideDelay: 7000,
                    variant: 'danger',
                    title: 'Error: Coordinates Out of Region.',
                    toaster: 'b-toaster-top-center'
                }
                )
                return
            }

            this.format = detectedFormat

            let x, y
            if (this.format) {
                y = a
                x = b
            } else {
                x = a
                y = b
            }

            let errX = x > 180 || x < -180
            let errY = y > 90 || y < -90
            let errMsg = 'Please Enter Valid '
            if (errX && errY) {
                errMsg += 'Latitude and Longitude'
            } else if (errX) {
                errMsg += 'Longitude'
            } else if (errY) {
                errMsg += 'Latitude'
            }
            errMsg += '.'
            if (errX || errY) {
                this.$bvToast.toast(errMsg, {
                autoHideDelay: 7000,
                title: 'Error: Invalid Coordinates.',
                toaster: 'b-toaster-top-right'
                })
                return
            }
            let newCenter = new transform([x, y], 'EPSG:4326', 'EPSG:3857')
            this.map.getView().animate({zoom: 18}, {center: newCenter})
            
            // Define custom style for the geoMarker
            const hexagonStyle = new Style({
                image: new RegularShape({
                points: 6, // Number of points for the hexagon
                radius: 7, // Radius of the hexagon
                fill: new Fill({ color: 'rgba(6, 60, 109, 0.2)' }), // Fill color
                stroke: new Stroke({ color: '#00008B', width: 2 }), // Border color and width
                }),
            });
            
            let geoMarker = new Feature({
                type: 'geoMarker',
                geometry: new Point(new fromLonLat([x, y])),
                id: "latLongPointer"
            })

            geoMarker.setStyle(hexagonStyle);

            await this.$emit('addPoint', geoMarker)
            await this.$emit('showToggle')
            
            this.lat = y
            this.lon = x

            // Closes the coordinate feature component
            document.getElementById('coordinatesDisplay').click()
        },
        copyCoords(newValue) {
            let lat = newValue[1].toFixed(6)
            let lon = newValue[0].toFixed(6)
            if (!(lat == 0 && lon == 0)) {
                navigator.clipboard.writeText(`${lat}, ${lon}`)
                this.$bvToast.toast('Coordinates copied to clipboard', {
                    autoHideDelay: 2000,
                    title: 'Coordinates Copied',
                    toaster: 'b-toaster-bottom-right'
                })
            }
        },
        parseCoordinateInput(input) {
            const matches = input.match(/-?\d+\.?\d*/g)
            if (!matches || matches.length < 2) return null
            return [parseFloat(matches[0]), parseFloat(matches[1])]
        },
        getRegionBounds() {
            let region = this.selectedExtent ? this.selectedExtent : 'North America'
            const table = {
                'North America':           { lonMin: -170, lonMax:  -50, latMin:  15, latMax:  75 },
                'Europe':                  { lonMin:  -25, lonMax:   45, latMin:  34, latMax:  72 },
                'Japan':                   { lonMin:  122, lonMax:  153, latMin:  24, latMax:  46 },
                'South Korea':             { lonMin:  124, lonMax:  130, latMin:  33, latMax:  39 },
                'Middle East':             { lonMin:   25, lonMax:   65, latMin:  12, latMax:  42 },
                'Kuwait':                  { lonMin:   46, lonMax:  48.5, latMin: 28.5, latMax: 30.1 },
                'Saudi Arabia':            { lonMin:   36, lonMax:   55, latMin:  16, latMax:  33 },
            }
            return table[region]
        },
        detectCoordFormat(a, b) {
            const bounds = this.getRegionBounds()
            if (!bounds) return this.format  // unknown region — keep current format
            const { lonMin, lonMax, latMin, latMax } = bounds
            const inRange = (v, lo, hi) => v >= lo && v <= hi
            const fitsLonLat = inRange(a, lonMin, lonMax) && inRange(b, latMin, latMax)
            const fitsLatLon = inRange(a, latMin, latMax) && inRange(b, lonMin, lonMax)
            if (fitsLatLon && !fitsLonLat) return true   // LAT/LON
            if (fitsLonLat && !fitsLatLon) return false  // LON/LAT
            if (fitsLatLon && fitsLonLat) return this.format  // ambiguous — keep current
            return null                                   // neither fits — out of region
        },
    },
    watch: {
        clickedCoords: function (newValue) {
            this.copyCoords(newValue)
        }
    }
}
</script>